import { useMemo, useState } from 'react';
import { Plus, Search, Network as NetworkIcon } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useReservedNetworks,
  useInstanceConfig,
  useCreateReservedNetwork,
  useDeleteReservedNetwork,
  type Network,
} from '@/api/opennebula';
import { RowMenu, attachedCount } from './networking-parts';

export function ReservedNetworks({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: networks = [], isLoading } = useReservedNetworks(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<Network | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return networks;
    return networks.filter((n) =>
      [n.name, n.id, n.ip, n.address, n.cidr].some((f) => String(f ?? '').toLowerCase().includes(q)),
    );
  }, [networks, query]);

  const remove = useDeleteReservedNetwork(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Reservation released' }); setDel(null); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  // Reserved blocks with active leases can't be released until detached.
  const askDelete = (n: Network) => {
    if (attachedCount(n) > 0) {
      show({ tone: 'danger', title: 'Detach all instances before releasing this reservation' });
      return;
    }
    setDel(n);
  };

  const columns: ColumnDef<Network, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'address', header: 'Address', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.ip || row.original.address || row.original.cidr || '—'}</span> },
    { id: 'size', header: 'Size', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.size ?? '—'}</span> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu onDelete={() => askDelete(row.original)} /> },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search reservations" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} />}
        end={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Reserve</Button>}
      />
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(n) => String(n.id)}
          emptyState={
            networks.length === 0 ? (
              <EmptyState
                icon={<NetworkIcon size={28} />}
                title="No reserved networks yet"
                description="Reserve an IP address from a network."
                actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Reserve</Button>}
              />
            ) : (
              'No reservations match your search.'
            )
          }
        />
      )}
      <ReserveNetworkDialog serviceId={serviceId} open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Release reservation?"
        description={`Release “${del?.name || del?.id}”?`}
        confirmLabel="Release"
        destructive
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </>
  );
}

// ---- IPv4 helpers (validate a chosen address sits inside the parent CIDR) ----

function ipv4ToInt(ip: string): number | null {
  const m = ip.trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return null;
  const o = m.slice(1).map(Number);
  if (o.some((n) => n > 255)) return null;
  return ((o[0] << 24) | (o[1] << 16) | (o[2] << 8) | o[3]) >>> 0;
}

/** True when `ip` is a valid IPv4 that falls within `cidr` (format-only if no CIDR). */
function ipInCidr(ip: string, cidr?: string): boolean {
  const ipi = ipv4ToInt(ip);
  if (ipi == null) return false;
  if (!cidr || !cidr.includes('/')) return true;
  const [base, bitsStr] = cidr.split('/');
  const basei = ipv4ToInt(base);
  const bits = Number(bitsStr);
  if (basei == null || !Number.isInteger(bits) || bits < 0 || bits > 32) return true;
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  return (ipi & mask) === (basei & mask);
}

/**
 * Reserve an address from an existing network. The backend
 * (`s10003/networks/reserves`) keys the reservation off `network_id` (a request
 * without it fails `Network.Invalid`) and optionally pins the exact `ip` — leave
 * it blank to let Opennebula auto-assign the next free lease. The selectable
 * networks come from the instance-config endpoint (same source as @console),
 * which lists every usable network, not just private ones.
 */
function ReserveNetworkDialog({
  serviceId,
  open,
  onOpenChange,
}: {
  serviceId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { show } = useToast();
  const { data: config } = useInstanceConfig(open ? serviceId : undefined);
  const networks = config?.networks ?? [];
  const [selected, setSelected] = useState('');
  const [ip, setIp] = useState('');
  const [size, setSize] = useState('1');

  const parent = networks.find((n) => String(n.id) === selected);
  const ipTrimmed = ip.trim();
  const ipValid = !ipTrimmed || ipInCidr(ipTrimmed, parent?.prefix);
  const sizeNum = Number(size);
  const sizeValid = Number.isInteger(sizeNum) && sizeNum >= 1;
  const canSubmit = !!selected && ipValid && sizeValid;

  const create = useCreateReservedNetwork(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Network reserved' }); close(false); },
    onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Reserve failed' }),
  });

  function close(next: boolean) {
    if (!next) { setSelected(''); setIp(''); setSize('1'); }
    onOpenChange(next);
  }

  function submit() {
    if (!canSubmit) return;
    create.mutate({ network_id: selected, ip: ipTrimmed || undefined, size: sizeNum });
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reserve network</DialogTitle>
          <DialogDescription>Reserve an IP address from a network for later use.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Network" required>
            {(p) =>
              networks.length === 0 ? (
                <Alert tone="info">No networks available to reserve from.</Alert>
              ) : (
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger {...p}><SelectValue placeholder="Select a network" /></SelectTrigger>
                  <SelectContent>
                    {networks.map((n) => {
                      const id = String(n.id);
                      return (
                        <SelectItem key={id} value={id}>
                          {n.name || id}{n.prefix ? ` (${n.prefix})` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )
            }
          </Field>
          <Field
            label="Start IP address"
            description={parent?.prefix ? `Within ${parent.prefix}. Leave blank to auto-assign.` : 'Leave blank to auto-assign the next free address.'}
            error={ipTrimmed && !ipValid ? 'Enter a valid IPv4 address inside the network range' : undefined}
          >
            {(p) => <Input {...p} value={ip} invalid={!!ipTrimmed && !ipValid} onChange={(e) => setIp(e.target.value)} placeholder="e.g. 10.0.3.10" />}
          </Field>
          <Field
            label="Size (addresses)"
            description="How many consecutive addresses to reserve, starting at the IP above."
            error={size.trim() && !sizeValid ? 'Enter a whole number ≥ 1' : undefined}
          >
            {(p) => <Input {...p} type="number" min={1} step={1} value={size} invalid={!!size.trim() && !sizeValid} onChange={(e) => setSize(e.target.value)} placeholder="1" />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => close(false)} disabled={create.isPending}>Cancel</Button>
          <Button disabled={!canSubmit || create.isPending} onClick={submit}>
            {create.isPending ? 'Reserving…' : 'Reserve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
